import { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { HiOutlineExternalLink, HiOutlineTrash } from 'react-icons/hi'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import DeleteConfirmModal from './DeleteConfirmModal'
import InterviewPreparationModal from './InterviewPreparationModal'
import ResumeOptimizationView from './ResumeOptimizationView'
import ResumeTemplateModal from './ResumeTemplateModal'
import { generateResume, getResumeGenerationStatus, reAnalyzeJob } from '../../services/api'
import { trackInterviewPrep } from '../../utils/analytics'
import { toTitleCase } from '../../utils/textFormatting'
import './JobPostingCard.scss'

// Note: Resume status is now fetched once in Dashboard and passed as a prop
// This prevents multiple API calls when multiple job cards are rendered

interface JobPostingCardProps {
  jobPosting: any
  onStateChange: (jobId: string, newState: string) => Promise<void>
  onDelete: (jobId: string) => Promise<void>
  getAccessTokenSilently: () => Promise<string>
  userData: any
  resumeStatus?: any | null
}

/**
 * Sanitize filename to be safe for file systems
 */
function sanitizeFilename(name: string): string {
  if (!name || typeof name !== 'string') {
    return ''
  }
  
  return name
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '') // Remove invalid chars and control chars
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/^[._]+|[._]+$/g, '') // Remove leading/trailing dots and underscores
    .trim()
    .substring(0, 100) // Limit length
}

/**
 * Generate resume filename with profile name and company
 */
function generateResumeFilename(profileName: string, companyName: string): string {
  const sanitizedProfileName = sanitizeFilename(profileName || '')
  const sanitizedCompanyName = sanitizeFilename(companyName || '')
  
  let filename = 'resume'
  
  if (sanitizedProfileName) {
    filename += `_${sanitizedProfileName}`
  }
  
  if (sanitizedCompanyName) {
    filename += `_${sanitizedCompanyName}`
  }
  
  // Ensure filename doesn't exceed 250 characters (leaving room for .docx)
  if (filename.length > 250) {
    filename = filename.substring(0, 250)
  }
  
  // Remove trailing underscores if any
  filename = filename.replace(/_+$/, '')
  
  // If we only have "resume" (no name or company), keep it simple
  if (filename === 'resume') {
    return 'resume.docx'
  }
  
  return `${filename}.docx`
}

function JobPostingCard({ jobPosting, onStateChange, onDelete, getAccessTokenSilently, userData, resumeStatus }: JobPostingCardProps) {
  const [isUpdating, setIsUpdating] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [isGeneratingResume, setIsGeneratingResume] = useState(false)
  const [showResumeView, setShowResumeView] = useState(false)
  const [resumeDownloadUrl, setResumeDownloadUrl] = useState<string | null>(null)
  const [resumeReportUrl, setResumeReportUrl] = useState<string | null>(null)
  const [resumeReady, setResumeReady] = useState(false)
  const [isReAnalyzing, setIsReAnalyzing] = useState(false)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isGeneratingRef = useRef(false) // Track if we're actively generating to prevent status check interference

  // Check if resume is already ready on mount for THIS specific job
  // Also check when job is in tailor_made state to ensure View Resume link shows
  // Only check for jobs in states where a resume might exist (analyzed, tailor_made, applied)
  // Update resume state based on the shared resumeStatus prop from Dashboard
  useEffect(() => {
    // Only check status for jobs that are in states where a resume might exist
    const shouldCheckStatus = ['analyzed', 'tailor_made', 'resume_created', 'applied'].includes(jobPosting.state)
    if (!shouldCheckStatus || !resumeStatus) {
      return
    }

    // Only show button if resume is completed AND it was generated for THIS job
    if (resumeStatus.status === 'completed' && 
        resumeStatus.downloadUrl && 
        resumeStatus.jobId === jobPosting.id) {
      setResumeDownloadUrl(resumeStatus.downloadUrl)
      setResumeReportUrl(resumeStatus.reportUrl || null)
      setResumeReady(true)
      setIsGeneratingResume(false)
      isGeneratingRef.current = false
    } else if (resumeStatus.status === 'processing' && resumeStatus.jobId === jobPosting.id) {
      // Resume is being generated for this job, keep generating state
      setIsGeneratingResume(true)
      isGeneratingRef.current = true
      setResumeReady(false)
    } else {
      // Reset state if resume is for a different job or not ready
      if (resumeStatus.jobId && resumeStatus.jobId !== jobPosting.id) {
        setResumeDownloadUrl(null)
        setResumeReportUrl(null)
        setResumeReady(false)
      } else if (resumeStatus.status !== 'processing') {
        setResumeReady(false)
      }
    }
  }, [resumeStatus, jobPosting.id, jobPosting.state])

  // Reset isGeneratingResume when resumeReady becomes true
  useEffect(() => {
    if (resumeReady) {
      setIsGeneratingResume(false)
    }
  }, [resumeReady])

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])

  const currentState = jobPosting.state || 'imported'
  
  // Clear resume state when job transitions to 'analyzed' from a previous state
  // This ensures old resumes don't make Tailor-Made active when re-analysis completes
  // We track the previous state to detect when job moves from 'imported' to 'analyzed'
  const prevStateRef = useRef(jobPosting.state || 'imported')
  useEffect(() => {
    // If state changed from 'imported' (or any non-analyzed state) to 'analyzed', clear resume state
    // This handles the re-analyze case where we want to start fresh
    if (currentState === 'analyzed' && prevStateRef.current !== 'analyzed' && prevStateRef.current !== 'tailor_made' && prevStateRef.current !== 'applied') {
      setResumeReady(false)
      setResumeDownloadUrl(null)
      setResumeReportUrl(null)
    }
    prevStateRef.current = currentState
  }, [currentState])
  // Use the actual current state for progress tracking, but map old states to new ones
  let displayState = currentState
  if (currentState === 'resume_created') {
    displayState = 'tailor_made'
  } else if (currentState === 'analyzing') {
    displayState = 'analyzed'
  }
  
  // Check if job is in analyzed state or beyond
  // Analyzed is complete only when state has moved beyond analyzed (to tailor_made or applied)
  // If currentState is still 'analyzed', it should remain active
  // During re-analysis, treat as not analyzed to disable buttons
  // IMPORTANT: Only check state, not resumeReady - state is the source of truth
  const isAnalyzed = !isReAnalyzing && (currentState === 'analyzed' || ['tailor_made', 'applied', 'resume_created'].includes(currentState))
  
  // Analyzed stage is active when currentState is 'analyzed', completed when state has moved beyond
  const isAnalyzedActive = currentState === 'analyzed'
  
  // Check if job is in tailor_made state (resume has been generated)
  // Tailor-Made should be active only when:
  // 1. State is 'tailor_made' or 'resume_created' (resume was generated)
  // 2. State is 'applied' (if applied, resume must have been ready - show as completed)
  // 3. State is 'analyzed' AND resumeReady is true (resume generated after analysis)
  // Note: We check state first to ensure we don't show Tailor-Made as active for old resumes
  const isTailorMade = ['tailor_made', 'resume_created', 'applied'].includes(currentState) || 
                       (currentState === 'analyzed' && resumeReady)
  
  // Tailor-Made is completed when state is 'applied' (can't apply without resume)
  const isTailorMadeCompleted = currentState === 'applied'

  const handleStateChange = async (newState: string) => {
    if (isUpdating) return
    
    setIsUpdating(true)
    try {
      await onStateChange(jobPosting.id, newState)
    } catch (error) {
      logger.error('Error updating state', {
        error: error instanceof Error ? error.message : String(error),
        jobId: jobPosting.id,
        newState,
      });
      Sentry.captureException(error, {
        tags: { component: 'JobPostingCard', function: 'handleStateChange' },
        extra: { jobId: jobPosting.id, newState },
      })
      toast.error('Failed to update job posting state. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleMarkAsApplied = async () => {
    await handleStateChange('applied')
  }

  const handleReAnalyze = async () => {
    if (isReAnalyzing || !jobPosting.id) return
    
    setIsReAnalyzing(true)
    try {
      // Clear resume state when re-analyzing
      setResumeReady(false)
      setResumeDownloadUrl(null)
      setResumeReportUrl(null)
      setIsGeneratingResume(false)
      isGeneratingRef.current = false
      
      // Call re-analyze endpoint (this already sets state to 'imported' and creates the activity)
      await reAnalyzeJob(jobPosting.id, getAccessTokenSilently)
      
      // Update the job state in parent component to refresh UI
      // The backend now checks if state changed before creating activity, so no duplicate will be created
      await onStateChange(jobPosting.id, 'imported')
      
      toast.success('Job re-analysis started. This usually takes 15-30 minutes.')
    } catch (error) {
      logger.error('Error re-analyzing job', {
        error: error instanceof Error ? error.message : String(error),
        jobId: jobPosting.id,
      });
      Sentry.captureException(error, {
        tags: { component: 'JobPostingCard', function: 'handleReAnalyze' },
        extra: { jobId: jobPosting.id },
      })
      const errorMessage = error instanceof Error ? error.message : 'Failed to re-analyze job'
      toast.error(`Error re-analyzing job: ${errorMessage}`)
    } finally {
      setIsReAnalyzing(false)
    }
  }


  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onDelete(jobPosting.id)
      setShowDeleteModal(false)
    } catch (error) {
      logger.error('Error deleting job posting', {
        error: error instanceof Error ? error.message : String(error),
        jobId: jobPosting.id,
      });
      Sentry.captureException(error, {
        tags: { component: 'JobPostingCard', function: 'handleDelete' },
        extra: { jobId: jobPosting.id },
      })
      toast.error('Failed to delete job posting. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateString: string | undefined): string | null => {
    if (!dateString) return null
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatRelativeDate = (dateString: string | undefined): string | null => {
    if (!dateString) return null
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  const getStateTimestamp = (stateKey: string): string | null => {
    const stateHistory = jobPosting.stateHistory || []
    // Try exact match first
    let entry = stateHistory.find(h => h.state === stateKey)
    if (entry) return entry.timestamp
    
    // Handle legacy state mappings
    if (stateKey === 'tailor_made') {
      entry = stateHistory.find(h => h.state === 'resume_created' || h.state === 'tailor_made')
      if (entry) return entry.timestamp
    }
    
    return null
  }

  const handleGenerateResume = () => {
    if (isGeneratingResume) return
    
    // Check if job has an ID
    if (!jobPosting.id) {
      toast.error('Job ID is missing. Cannot generate resume.')
      return
    }

    // Show template selection modal
    setShowTemplateModal(true)
  }

  const handleTemplateSelected = async (templateId: string) => {
    setShowTemplateModal(false)

    // Clear old resume state when regenerating
    setResumeReady(false)
    setResumeDownloadUrl(null)
    setResumeReportUrl(null)
    setIsGeneratingResume(true)
    isGeneratingRef.current = true
    
    return Sentry.startSpan(
      {
        op: "ui.click",
        name: "Generate Resume",
      },
      async (span) => {
        span.setAttribute("jobId", jobPosting.id!)
        span.setAttribute("templateId", templateId)
        
        try {
          const result = await generateResume(
            jobPosting.id!,
            templateId,
            getAccessTokenSilently
          )

          // Check if we got a cached resume immediately
          if ('blob' in result && result.blob instanceof Blob) {
            // For cached resumes, verify it's for this job and create a download URL
            const profileName = userData?.contactInfo?.name || ''
            const companyName = jobPosting.company || ''
            const filename = generateResumeFilename(profileName, companyName)
            
            // Verify this resume is for the current job
            try {
              const response = await getResumeGenerationStatus(getAccessTokenSilently, jobPosting.id)
              // Handle both old format (single status) and new format (statuses map)
              const statusResult = 'statuses' in response 
                ? (response.statuses[jobPosting.id] || null)
                : (response as any)
              // Only proceed if this resume is for the current job
              if (statusResult && statusResult.status === 'completed') {
                const url = window.URL.createObjectURL(result.blob)
                setResumeDownloadUrl(url)
                setResumeReportUrl(statusResult.reportUrl || null)
                setResumeReady(true)
                toast.success('Resume generated successfully! Click "View Resume" to see the changes.')
              } else {
                // Resume is for a different job, download it but don't show view button
                const url = window.URL.createObjectURL(result.blob)
                const a = document.createElement('a')
                a.href = url
                a.download = filename
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                window.URL.revokeObjectURL(url)
                toast.success('Resume downloaded successfully!')
              }
            } catch (err) {
              logger.error('Error fetching status for cached resume', {
                error: err instanceof Error ? err.message : String(err),
                jobId: jobPosting.id,
                templateId,
              });
              Sentry.captureException(err, {
                tags: { component: 'JobPostingCard', function: 'handleTemplateSelected', errorType: 'status_check' },
                extra: { jobId: jobPosting.id, templateId },
              })
              // Fallback: download the resume
              const url = window.URL.createObjectURL(result.blob)
              const a = document.createElement('a')
              a.href = url
              a.download = filename
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              window.URL.revokeObjectURL(url)
              toast.success('Resume downloaded successfully!')
            }
            setIsGeneratingResume(false)
            isGeneratingRef.current = false
            return
          }

          // Resume generation was queued, start polling
          if (result.status === 'processing') {
            // Poll every 10 seconds for status
            const pollInterval = 10000 // 10 seconds
            const maxPollAttempts = 60 // Stop after 10 minutes (60 * 10s)
            let pollAttempts = 0

            const pollStatus = async () => {
              pollAttempts++
              
              try {
                // Poll for this specific job's status
                const response = await getResumeGenerationStatus(getAccessTokenSilently, jobPosting.id)
                // Handle both old format (single status) and new format (statuses map)
                const statusResult = 'statuses' in response 
                  ? (response.statuses[jobPosting.id] || null)
                  : (response as any)
                
                if (!statusResult) {
                  // No status for this job yet, continue polling
                  return
                }
                
                if (statusResult.status === 'completed' && statusResult.downloadUrl) {
                  // Resume is ready for this job
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current)
                    pollIntervalRef.current = null
                  }
                  
                  // Store URLs and mark resume as ready
                  setResumeDownloadUrl(statusResult.downloadUrl)
                  setResumeReportUrl(statusResult.reportUrl || null)
                  setResumeReady(true)
                  
                  toast.success('Resume generated successfully! Click "View Resume" to see the changes.')
                  setIsGeneratingResume(false)
                  isGeneratingRef.current = false
                } else if (statusResult.status === 'error') {
                  // Generation failed
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current)
                    pollIntervalRef.current = null
                  }
                  const errorMessage = statusResult.error || 'Resume generation failed'
                  toast.error(`Error generating resume: ${errorMessage}`)
                  setIsGeneratingResume(false)
                  isGeneratingRef.current = false
                } else if (pollAttempts >= maxPollAttempts) {
                  // Timeout
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current)
                    pollIntervalRef.current = null
                  }
                  toast.warning('Resume generation is taking longer than expected. Please try again later.')
                  setIsGeneratingResume(false)
                  isGeneratingRef.current = false
                }
                // Otherwise, continue polling
              } catch (pollError) {
                logger.error('Error polling resume status', {
                  error: pollError instanceof Error ? pollError.message : String(pollError),
                  jobId: jobPosting.id,
                  pollAttempts,
                });
                Sentry.captureException(pollError, {
                  tags: { component: 'JobPostingCard', function: 'pollStatus' },
                  extra: { jobId: jobPosting.id, pollAttempts },
                })
                if (pollAttempts >= maxPollAttempts) {
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current)
                    pollIntervalRef.current = null
                  }
                  toast.error('Error checking resume generation status. Please try again later.')
                  setIsGeneratingResume(false)
                  isGeneratingRef.current = false
                }
              }
            }

            // Start polling
            pollIntervalRef.current = setInterval(pollStatus, pollInterval)
            // Also poll immediately
            pollStatus()
          } else {
            // Unexpected status
            toast.error(`Unexpected response: ${result.message || 'Unknown status'}`)
            setIsGeneratingResume(false)
            isGeneratingRef.current = false
          }
        } catch (error) {
          logger.error('Error generating resume', {
            error: error instanceof Error ? error.message : String(error),
            jobId: jobPosting.id,
          });
          Sentry.captureException(error, {
            tags: { component: 'JobPostingCard', function: 'handleTemplateSelected' },
            extra: { jobId: jobPosting.id },
          })
          const errorMessage = error instanceof Error ? error.message : 'Failed to generate resume'
          toast.error(`Error generating resume: ${errorMessage}`)
          setIsGeneratingResume(false)
          isGeneratingRef.current = false
        }
      }
    )
  }

  return (
    <div className="job-posting-card">
      <div className="job-posting-header">
        <div className="job-posting-title-section">
          <h4 className="job-posting-title">{toTitleCase(jobPosting.title) || 'Untitled Job Posting'}</h4>
          <div className="job-posting-meta">
            <span className="job-company">{jobPosting.company || 'Unknown Company'}</span>
            {jobPosting.companyWebsite && (
              <>
                <span className="meta-separator">•</span>
                <a 
                  href={`https://${jobPosting.companyWebsite}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="job-company-website"
                  onClick={(e) => e.stopPropagation()}
                >
                  {jobPosting.companyWebsite}
                </a>
              </>
            )}
            {jobPosting.location && (
              <>
                <span className="meta-separator">•</span>
                <span className="job-location">{jobPosting.location}</span>
              </>
            )}
          </div>
        </div>
        <div className="job-posting-header-actions">
          {jobPosting.url && jobPosting.url.trim() && (
            <a 
              href={jobPosting.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="job-posting-link"
              title="View original posting"
            >
              <HiOutlineExternalLink />
            </a>
          )}
          <button
            className="job-posting-delete-btn"
            onClick={() => setShowDeleteModal(true)}
            title="Delete job posting"
            disabled={isDeleting}
          >
            <HiOutlineTrash />
          </button>
        </div>
      </div>

      {(jobPosting.short_description || jobPosting.description) && (
        <div className="job-posting-description">
          <p>{jobPosting.short_description || jobPosting.description?.substring(0, 200)}{!jobPosting.short_description && jobPosting.description && jobPosting.description.length > 200 ? '...' : ''}</p>
        </div>
      )}

      <div className="job-actions-timeline">
        {/* Import Stage - Always shown */}
        <div className={`timeline-stage ${currentState === 'imported' ? 'active' : 'completed'}`}>
          <div className="timeline-marker">
            <div className="timeline-dot"></div>
            <div className="timeline-line"></div>
          </div>
          <div className="timeline-content">
            <div className="timeline-stage-header">
              <div>
                <h5 className="timeline-stage-title">Imported</h5>
                {getStateTimestamp('imported') && (
                  <div className="timeline-stage-time">
                    {formatRelativeDate(getStateTimestamp('imported'))}
                  </div>
                )}
              </div>
              {currentState === 'imported' ? (
                <span className="timeline-stage-badge">Active</span>
              ) : (
                <span className="timeline-stage-badge completed">Completed</span>
              )}
            </div>
            {currentState === 'imported' && (
              <div className="timeline-stage-description">
                {isReAnalyzing 
                  ? 'Re-analyzing job with updated profile information. This usually takes 5-15 minutes.'
                  : 'Job posting imported. Analysis in progress to provide application insights. This usually takes 5-15 minutes.'}
              </div>
            )}
          </div>
        </div>

        {/* Analyzed Stage */}
        <div className={`timeline-stage ${isAnalyzed ? (isAnalyzedActive ? 'active' : 'completed') : 'upcoming'}`}>
          <div className="timeline-marker">
            <div className="timeline-dot"></div>
            <div className="timeline-line"></div>
          </div>
          <div className="timeline-content">
            <div className="timeline-stage-header">
              <div>
                <h5 className="timeline-stage-title">Analyzed</h5>
                {getStateTimestamp('analyzed') && (
                  <div className="timeline-stage-time">
                    {formatRelativeDate(getStateTimestamp('analyzed'))}
                  </div>
                )}
              </div>
              {isAnalyzed ? (
                isAnalyzedActive ? (
                  <span className="timeline-stage-badge">Active</span>
                ) : (
                  <span className="timeline-stage-badge completed">Completed</span>
                )
              ) : (
                <span className="timeline-stage-badge upcoming">Upcoming</span>
              )}
            </div>
            {isAnalyzed && (
              <>
                <div className="timeline-stage-description">
                  Job analysis complete. Prepare for interviews and generate your tailored resume.
                </div>
                <div className="timeline-actions">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      trackInterviewPrep(jobPosting.title, jobPosting.company)
                      setShowInterviewModal(true)
                    }}
                    disabled={isReAnalyzing}
                  >
                    📚 Interview Preparation
                  </button>
                  
                  {(jobPosting.description || jobPosting.short_description) && (
                    <button 
                      className={`btn btn-primary btn-sm ${isGeneratingResume ? 'btn-generating' : ''}`}
                      onClick={handleGenerateResume}
                      disabled={isGeneratingResume || isReAnalyzing}
                      title={resumeReady ? "Re-generate optimized resume for this job" : "Generate optimized resume for this job"}
                    >
                      {isGeneratingResume ? (
                        <span>
                          <span>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            <span>Generating...</span>
                          </span>
                          <span style={{ fontSize: '0.75em' }}>This usually takes 10-15 minutes...</span>
                        </span>
                      ) : (
                        <>
                          📄 {resumeReady ? 'Re-generate Resume' : 'Generate Resume'}
                        </>
                      )}
                    </button>
                  )}
                  
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleReAnalyze}
                    disabled={isReAnalyzing || currentState === 'imported'}
                    title="Re-run analysis with updated profile information"
                  >
                    {isReAnalyzing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Re-analyzing...
                      </>
                    ) : (
                      '🔄 Re-analyze Job'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tailor-Made Stage */}
        <div className={`timeline-stage ${isTailorMade ? (isTailorMadeCompleted ? 'completed' : 'active') : 'upcoming'}`}>
          <div className="timeline-marker">
            <div className="timeline-dot"></div>
            <div className="timeline-line"></div>
          </div>
          <div className="timeline-content">
            <div className="timeline-stage-header">
              <div>
                <h5 className="timeline-stage-title">Tailor-Made</h5>
                {getStateTimestamp('tailor_made') && (
                  <div className="timeline-stage-time">
                    {formatRelativeDate(getStateTimestamp('tailor_made'))}
                  </div>
                )}
              </div>
              {isTailorMade ? (
                isTailorMadeCompleted ? (
                  <span className="timeline-stage-badge completed">Completed</span>
                ) : (
                  <span className="timeline-stage-badge">Active</span>
                )
              ) : (
                <span className="timeline-stage-badge upcoming">Upcoming</span>
              )}
            </div>
            {isTailorMade && (
              <>
                <div className="timeline-stage-description">
                  {isGeneratingResume 
                    ? 'Your resume is being generated. This usually takes 10-15 minutes.'
                    : 'Your tailored resume is ready. Review it, apply for the position, then mark it as applied.'}
                </div>
                {/* Show actions if resume is ready OR if tailor-made is completed (applied state - resume must exist) */}
                {(resumeReady || isTailorMadeCompleted || resumeDownloadUrl || isGeneratingResume) && (
                  <div className="timeline-actions">
                    {(resumeDownloadUrl || isTailorMadeCompleted) && !isGeneratingResume && (
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowResumeView(true)}
                        title="View resume and optimization report"
                      >
                        📄 View Resume
                      </button>
                    )}
                    
                    {isGeneratingResume && (
                      <button 
                        className="btn btn-primary btn-sm"
                        disabled={true}
                        title="Resume is being generated"
                      >
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating Resume...
                      </button>
                    )}
                    
                    {!isTailorMadeCompleted && !isGeneratingResume && (
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={handleMarkAsApplied}
                        disabled={isUpdating || !resumeReady}
                        title={!resumeReady ? "Resume must be ready before marking as applied" : "Mark this job as applied"}
                      >
                        ✉️ Mark as Applied
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Applied Stage */}
        <div className={`timeline-stage ${currentState === 'applied' ? 'active' : 'upcoming'}`}>
          <div className="timeline-marker">
            <div className="timeline-dot"></div>
          </div>
          <div className="timeline-content">
            <div className="timeline-stage-header">
              <div>
                <h5 className="timeline-stage-title">Success!</h5>
                {getStateTimestamp('applied') && (
                  <div className="timeline-stage-time">
                    {formatRelativeDate(getStateTimestamp('applied'))}
                  </div>
                )}
              </div>
              {currentState === 'applied' ? (
                <span className="timeline-stage-badge">Active</span>
              ) : (
                <span className="timeline-stage-badge upcoming">Upcoming</span>
              )}
            </div>
            {currentState === 'applied' && (
              <div className="timeline-stage-description">
                Application submitted. Waiting for response from the employer.
              </div>
            )}
          </div>
        </div>
      </div>

      {isUpdating && (
        <div className="updating-indicator">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Updating...</span>
          </div>
          <span>Updating status...</span>
        </div>
      )}

      <div className="job-posting-footer">
        <small className="text-muted">
          Imported: {formatDate(jobPosting.createdAt) || 'Unknown'}
        </small>
        {jobPosting.updatedAt && jobPosting.updatedAt !== jobPosting.createdAt && (
          <small className="text-muted">
            Last updated: {formatDate(jobPosting.updatedAt)}
          </small>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        jobTitle={jobPosting.title}
        isDeleting={isDeleting}
      />

      <InterviewPreparationModal
        isOpen={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        jobPosting={jobPosting}
        userData={userData}
        getAccessTokenSilently={getAccessTokenSilently}
      />

      <ResumeTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelectTemplate={handleTemplateSelected}
      />

      {showResumeView && (
        <ResumeOptimizationView
          downloadUrl={resumeDownloadUrl}
          reportUrl={resumeReportUrl}
          onClose={async () => {
            setShowResumeView(false)
            // Clean up blob URL if it was created from a blob
            // Re-fetch the server URL to keep the button visible
            if (resumeDownloadUrl && resumeDownloadUrl.startsWith('blob:')) {
              window.URL.revokeObjectURL(resumeDownloadUrl)
              // Re-fetch the resume status to get the server URL
              try {
                const response = await getResumeGenerationStatus(getAccessTokenSilently, jobPosting.id)
                // Handle both old format (single status) and new format (statuses map)
                const statusResult = 'statuses' in response 
                  ? (response.statuses[jobPosting.id] || null)
                  : (response as any)
                if (statusResult && statusResult.status === 'completed' && 
                    statusResult.downloadUrl) {
                  setResumeDownloadUrl(statusResult.downloadUrl)
                  setResumeReportUrl(statusResult.reportUrl || null)
                }
              } catch (err) {
                logger.error('Error re-fetching resume status', {
                  error: err instanceof Error ? err.message : String(err),
                  jobId: jobPosting.id,
                });
                Sentry.captureException(err, {
                  tags: { component: 'JobPostingCard', function: 'resume_status_refetch' },
                  extra: { jobId: jobPosting.id },
                })
              }
            }
          }}
          jobTitle={jobPosting.title}
          companyName={jobPosting.company}
          getAccessTokenSilently={getAccessTokenSilently}
        />
      )}

    </div>
  )
}

export default JobPostingCard
